import { ElementType, forwardRef, HTMLAttributes } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, VariantProps } from 'class-variance-authority'
import parse, { domToReact } from 'html-react-parser'

import { nl2br, removeParagraph } from '@/utils/strings'

import { cn } from '@/lib/utils'

const typographyVariants = cva('', {
  variants: {
    variant: {
      h1: 'font-base text-s-h1 lg:text-d-h1 group-[.modal]:text-s-h2 lg:group-[.modal]:text-d-h2 [&_em]:font-serif [&_em]:text-s-h1-serif lg:[&_em]:text-d-h1-serif [&_strong]:font-normal',
      h2: 'font-base text-s-h2 lg:text-d-h2 group-[.modal]:text-s-h3 lg:group-[.modal]:text-d-h3 [&_em]:font-serif [&_em]:text-s-h2-serif lg:[&_em]:text-d-h2-serif [&_strong]:font-normal',
      h3: 'font-base text-s-h3 lg:text-d-h3 group-[.modal]:text-s-h4 lg:group-[.modal]:text-d-h4 [&_em]:font-serif [&_em]:font-normal [&_strong]:font-normal',
      body: 'text-body',
      'body-strong': 'text-body font-bold',
      'menu-sans': 'text-s-h2 lg:text-menu-sans',
      'menu-serif': 'text-s-h2 lg:text-menu-serif italic font-serif',
      small: 'text-small',
      label: 'text-small uppercase',
      footer: 'text-footer'
    }
  },
  defaultVariants: {
    variant: 'body'
  }
})

const childrenClasses = cn([
  '[&_h2]:font-base [&_h2]:text-s-h6 [&_h2]:lg:text-d-h6',
  '[&_p]:text-post',
  '[&_ul]:flex [&_ul]:list-disc [&_ul]:flex-col [&_ul]:gap-4 [&_ul]:pl-6',
  '[&_ol]:flex [&_ol]:list-decimal [&_ol]:flex-col [&_ol]:gap-4 [&_ol]:pl-6',
  '[&_a]:underline [&_a]:transition-colors [&_a]:duration-300 [&_a]:ease-out-cubic'
])

export type TypographyProps = HTMLAttributes<HTMLElement> &
  VariantProps<typeof typographyVariants> &
  Partial<{
    component: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span'
    paragraph: boolean
    asChild: boolean
  }>

const options = {
  replace: (domNode: any) => {
    if (domNode.type === 'tag' && ['em', 'strong'].includes(domNode.name)) {
      const Tag = domNode.name
      return (
        <>
          {domNode.children.map((child: any, index: number) => {
            if (child.type === 'text') {
              return child.data
                .split(' ')
                .map((word: string, wordIndex: number) => (
                  <Tag key={`${index}-${wordIndex}`}>
                    {word}
                    {wordIndex < child.data.split(' ').length - 1 ? ' ' : ''}
                  </Tag>
                ))
            }
            return domToReact(child, options)
          })}
        </>
      )
    }
  }
}

const Typography = forwardRef<HTMLElement, TypographyProps>(
  (
    {
      variant,
      className,
      children,
      component = false,
      paragraph = false,
      asChild = false,
      ...props
    },
    ref
  ) => {
    const Component = asChild
      ? Slot
      : ((component || (paragraph ? 'p' : 'span')) as ElementType)

    return (
      <Component
        ref={ref}
        className={cn(
          typographyVariants({ variant, className }),
          childrenClasses
        )}
        {...props}
      >
        {parse(
          children ? removeParagraph(nl2br(children as string)) : '',
          options
        )}
      </Component>
    )
  }
)

export default Typography
